import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Loader2, SlidersHorizontal, Lock, Building2, UserCog } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_LABELS = {
  standard: 'Standard filiale',
  unique_rate: 'Taux unique',
  custom_tiers: 'Paliers spécifiques',
  semi_fixed: 'Semi-fixe (taux + €)',
  full_fixed: '100% fixe',
};
const MODE_COLORS = {
  standard: 'bg-slate-100 text-slate-600',
  unique_rate: 'bg-indigo-100 text-indigo-700',
  custom_tiers: 'bg-violet-100 text-violet-700',
  semi_fixed: 'bg-amber-100 text-amber-700',
  full_fixed: 'bg-emerald-100 text-emerald-700',
};

const emptyTcsu = { mode: 'standard', rate: '', tiers: null, fixed_sale: '', fixed_scope: 'sales', fixed_recruitment: '' };

const TcsuDialog = ({ open, onOpenChange, target, canEdit, onSaved }) => {
  const [cfg, setCfg] = useState(emptyTcsu);
  const [saving, setSaving] = useState(false);
  const isCR = target?.role === 'rh';

  useEffect(() => {
    if (target) {
      const t = target.tcsu || { mode: 'standard' };
      setCfg({
        mode: t.mode || 'standard',
        rate: t.rate ?? '',
        tiers: t.tiers || null,
        fixed_sale: t.fixed_sale ?? '',
        fixed_scope: t.fixed_scope || 'sales',
        fixed_recruitment: t.fixed_recruitment ?? '',
      });
    }
  }, [target]);

  const tiers = cfg.tiers || { rates: { seuil_1: '', seuil_2: '', seuil_3: '' }, thresholds: { tier_1_max: '', tier_2_max: '' } };
  const setTier = (grp, key, val) => {
    const next = {
      rates: { ...(tiers.rates || {}) },
      thresholds: { ...(tiers.thresholds || {}) },
    };
    next[grp][key] = val === '' ? '' : parseFloat(val);
    setCfg((c) => ({ ...c, tiers: next }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        mode: cfg.mode,
        rate: cfg.rate === '' ? null : parseFloat(cfg.rate),
        tiers: cfg.mode === 'custom_tiers' ? {
          rates: {
            seuil_1: parseFloat(tiers.rates.seuil_1) || 0,
            seuil_2: parseFloat(tiers.rates.seuil_2) || 0,
            seuil_3: parseFloat(tiers.rates.seuil_3) || 0,
          },
          thresholds: {
            tier_1_max: parseFloat(tiers.thresholds.tier_1_max) || 0,
            tier_2_max: parseFloat(tiers.thresholds.tier_2_max) || 0,
          },
        } : null,
        fixed_sale: cfg.fixed_sale === '' ? 0 : parseFloat(cfg.fixed_sale),
        fixed_scope: cfg.fixed_scope,
        fixed_recruitment: cfg.fixed_recruitment === '' ? 0 : parseFloat(cfg.fixed_recruitment),
      };
      const r = await fetch(`${API}/admin/tcsu/users/${target.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        toast.success('TCSU mis à jour');
        onSaved();
        onOpenChange(false);
      } else {
        const e = await r.json();
        toast.error(e.detail || 'Erreur');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!target) return null;
  const showFixedScope = isCR && (cfg.mode === 'semi_fixed' || cfg.mode === 'full_fixed');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="tcsu-dialog">
        <DialogHeader>
          <DialogTitle>Régime de commission — {target.name}</DialogTitle>
          <DialogDescription>
            {isCR ? 'CR (recruteur)' : 'AC (commercial)'} · {target.agency_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Régime</Label>
            <Select value={cfg.mode} onValueChange={(v) => setCfg((c) => ({ ...c, mode: v }))} disabled={!canEdit}>
              <SelectTrigger data-testid="tcsu-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard filiale (défaut)</SelectItem>
                <SelectItem value="unique_rate">Taux unique</SelectItem>
                <SelectItem value="custom_tiers">Paliers spécifiques</SelectItem>
                <SelectItem value="semi_fixed">Semi-fixe (taux + montant €)</SelectItem>
                <SelectItem value="full_fixed">100% fixe (montant € / vente)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {cfg.mode === 'unique_rate' && (
            <div className="space-y-2">
              <Label>Taux unique (%)</Label>
              <Input type="number" value={cfg.rate} onChange={(e) => setCfg((c) => ({ ...c, rate: e.target.value }))}
                placeholder="ex: 15" disabled={!canEdit} data-testid="tcsu-rate-input" />
            </div>
          )}

          {cfg.mode === 'custom_tiers' && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Taux par palier (basés sur le CA)</p>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs">Seuil 1 (%)</Label><Input type="number" value={tiers.rates.seuil_1} onChange={(e) => setTier('rates', 'seuil_1', e.target.value)} disabled={!canEdit} /></div>
                <div><Label className="text-xs">Seuil 2 (%)</Label><Input type="number" value={tiers.rates.seuil_2} onChange={(e) => setTier('rates', 'seuil_2', e.target.value)} disabled={!canEdit} /></div>
                <div><Label className="text-xs">Seuil 3 (%)</Label><Input type="number" value={tiers.rates.seuil_3} onChange={(e) => setTier('rates', 'seuil_3', e.target.value)} disabled={!canEdit} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Palier 1 max (€)</Label><Input type="number" value={tiers.thresholds.tier_1_max} onChange={(e) => setTier('thresholds', 'tier_1_max', e.target.value)} disabled={!canEdit} /></div>
                <div><Label className="text-xs">Palier 2 max (€)</Label><Input type="number" value={tiers.thresholds.tier_2_max} onChange={(e) => setTier('thresholds', 'tier_2_max', e.target.value)} disabled={!canEdit} /></div>
              </div>
            </div>
          )}

          {cfg.mode === 'semi_fixed' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Taux (%) — laisser vide pour utiliser le standard filiale</Label>
                <Input type="number" value={cfg.rate} onChange={(e) => setCfg((c) => ({ ...c, rate: e.target.value }))}
                  placeholder="standard si vide" disabled={!canEdit} />
              </div>
              <div className="space-y-2">
                <Label>Montant fixe par vente (€)</Label>
                <Input type="number" value={cfg.fixed_sale} onChange={(e) => setCfg((c) => ({ ...c, fixed_sale: e.target.value }))}
                  placeholder="ex: 300" disabled={!canEdit} data-testid="tcsu-fixedsale-input" />
              </div>
            </div>
          )}

          {cfg.mode === 'full_fixed' && (
            <div className="space-y-2">
              <Label>Montant fixe par vente / contrat (€)</Label>
              <Input type="number" value={cfg.fixed_sale} onChange={(e) => setCfg((c) => ({ ...c, fixed_sale: e.target.value }))}
                placeholder="ex: 2000" disabled={!canEdit} data-testid="tcsu-fullfixed-input" />
            </div>
          )}

          {showFixedScope && (
            <div className="space-y-3 rounded-lg bg-slate-50 p-3 border">
              <div className="space-y-2">
                <Label>Le fixe s'applique (CR)</Label>
                <Select value={cfg.fixed_scope} onValueChange={(v) => setCfg((c) => ({ ...c, fixed_scope: v }))} disabled={!canEdit}>
                  <SelectTrigger data-testid="tcsu-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">Sur les ventes</SelectItem>
                    <SelectItem value="recruitment">Sur les recrutements</SelectItem>
                    <SelectItem value="both">Les deux</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(cfg.fixed_scope === 'recruitment' || cfg.fixed_scope === 'both') && (
                <div className="space-y-2">
                  <Label>Montant fixe par recrutement (€)</Label>
                  <Input type="number" value={cfg.fixed_recruitment} onChange={(e) => setCfg((c) => ({ ...c, fixed_recruitment: e.target.value }))}
                    placeholder="ex: 500" disabled={!canEdit} data-testid="tcsu-recruitment-input" />
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={saving || !canEdit} data-testid="tcsu-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const TCSU = () => {
  const { isGroupSA } = useAuth();
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agencies, setAgencies] = useState([]);
  const [flags, setFlags] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState('all');
  const [users, setUsers] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadUsers = useCallback(async () => {
    const q = isGroupSA && selectedAgency !== 'all' ? `?agency_id=${selectedAgency}` : '';
    const r = await fetch(`${API}/admin/tcsu/users${q}`, { credentials: 'include' });
    if (r.ok) {
      const d = await r.json();
      setUsers(d.users || []);
      setCanEdit(d.can_edit);
    } else {
      setUsers([]);
    }
  }, [isGroupSA, selectedAgency]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const ar = await fetch(`${API}/tcsu/access`, { credentials: 'include' });
      const acc = ar.ok ? await ar.json() : { can_view: false };
      setAccess(acc);
      if (isGroupSA) {
        const [agRes, flRes] = await Promise.all([
          fetch(`${API}/agencies`, { credentials: 'include' }),
          fetch(`${API}/admin/tcsu/agencies`, { credentials: 'include' }),
        ]);
        if (agRes.ok) setAgencies(await agRes.json());
        if (flRes.ok) setFlags((await flRes.json()).agencies || []);
      }
      setLoading(false);
    })();
  }, [isGroupSA]);

  useEffect(() => { if (access?.can_view || isGroupSA) loadUsers(); }, [access, isGroupSA, selectedAgency, loadUsers]);

  const updateFlag = async (agencyId, field, value) => {
    const r = await fetch(`${API}/admin/tcsu/agency-flags`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ agency_id: agencyId, [field]: value }),
    });
    if (r.ok) {
      setFlags((prev) => prev.map((f) => f.agency_id === agencyId ? { ...f, [field]: value } : f));
      toast.success('Paramètre mis à jour');
      loadUsers();
    } else {
      toast.error('Erreur');
    }
  };

  const openEdit = (u) => { setEditTarget(u); setDialogOpen(true); };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  if (!isGroupSA && !access?.can_view) {
    return (
      <Card className="border-0 shadow-sm max-w-xl mx-auto mt-10" data-testid="tcsu-disabled">
        <CardContent className="p-8 text-center">
          <Lock className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">TCSU désactivé pour votre filiale</p>
          <p className="text-sm text-slate-400 mt-1">Contactez le Super Admin pour l'activer.</p>
        </CardContent>
      </Card>
    );
  }

  const acList = users.filter((u) => u.role === 'commercial');
  const crList = users.filter((u) => u.role === 'rh');

  const UserTable = ({ list, emptyLabel }) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead>Nom</TableHead>
            {isGroupSA && <TableHead>Filiale</TableHead>}
            <TableHead>Régime actuel</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.map((u) => (
            <TableRow key={u.user_id} data-testid={`tcsu-user-${u.user_id}`}>
              <TableCell>
                <p className="font-medium text-slate-900">{u.name}</p>
                <p className="text-xs text-slate-400">{u.email}</p>
              </TableCell>
              {isGroupSA && <TableCell className="text-sm text-slate-600">{u.agency_name}</TableCell>}
              <TableCell>
                <Badge className={MODE_COLORS[u.tcsu?.mode || 'standard']}>
                  {MODE_LABELS[u.tcsu?.mode || 'standard']}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`tcsu-config-${u.user_id}`}>
                  <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" /> Configurer
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {list.length === 0 && (
            <TableRow><TableCell colSpan={isGroupSA ? 4 : 3} className="text-center text-slate-400 py-6">{emptyLabel}</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="tcsu-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]">TCSU</h1>
        <p className="text-slate-500 mt-1">Taux de Commission Spécifique Unique — par AC / CR</p>
      </div>

      {!canEdit && access?.can_view && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm px-4 py-3 flex items-center gap-2" data-testid="tcsu-readonly-banner">
          <Lock className="w-4 h-4" /> Lecture seule — seul le Super Admin peut modifier les régimes de cette filiale.
        </div>
      )}

      {/* SA Groupe: filiale flags */}
      {isGroupSA && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-500" />
              <div>
                <CardTitle className="text-lg">Activation par filiale</CardTitle>
                <CardDescription>Activez le TCSU et l'accès des SOA, filiale par filiale</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {flags.map((f) => (
                <div key={f.agency_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50" data-testid={`tcsu-flag-${f.agency_id}`}>
                  <span className="font-medium text-slate-800">{f.name}</span>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <Switch checked={f.tcsu_agency_enabled} onCheckedChange={(v) => updateFlag(f.agency_id, 'tcsu_agency_enabled', v)} data-testid={`tcsu-toggle-agency-${f.agency_id}`} />
                      TCSU activé
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <Switch checked={f.tcsu_soa_enabled} disabled={!f.tcsu_agency_enabled} onCheckedChange={(v) => updateFlag(f.agency_id, 'tcsu_soa_enabled', v)} data-testid={`tcsu-toggle-soa-${f.agency_id}`} />
                      SOA peut modifier
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Agency filter for SA Groupe */}
      {isGroupSA && (
        <div className="flex items-center gap-3">
          <Label className="text-sm text-slate-500">Filiale :</Label>
          <Select value={selectedAgency} onValueChange={setSelectedAgency}>
            <SelectTrigger className="w-64" data-testid="tcsu-agency-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les filiales</SelectItem>
              {agencies.map((a) => <SelectItem key={a.agency_id} value={a.agency_id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <Tabs defaultValue="ac">
            <TabsList>
              <TabsTrigger value="ac" data-testid="tcsu-tab-ac"><UserCog className="w-4 h-4 mr-1.5" /> AC ({acList.length})</TabsTrigger>
              <TabsTrigger value="cr" data-testid="tcsu-tab-cr"><UserCog className="w-4 h-4 mr-1.5" /> CR ({crList.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="ac" className="mt-4"><UserTable list={acList} emptyLabel="Aucun AC" /></TabsContent>
            <TabsContent value="cr" className="mt-4"><UserTable list={crList} emptyLabel="Aucun CR" /></TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <TcsuDialog open={dialogOpen} onOpenChange={setDialogOpen} target={editTarget} canEdit={canEdit} onSaved={loadUsers} />
    </div>
  );
};

export default TCSU;
